import React, { Component } from "react"
import { Modal, Row, Col, Button, Form } from "react-bootstrap"
import { FaPlus, FaMinus } from "react-icons/fa"
import { toast } from "react-toastify"
import { EmailRegexp, PhoneRegexp } from "../../config/Constants"
import Checkbox from "../Checkbox"
import AssociatedData from "../../api/Administration/Model/AssociatedData"
import PhoneNumberMaskedInput from "../FormattedInputs/PhoneNumberMaskedInput"
import FormatPhone from "../../formatters/FormatPhone"
import ConfirmationModal from "../ConfirmationModal"

export const EmptyAssociatedData: AssociatedData = {
  Active: false,
  ApplicationUserID: 0,
  ContactDetail: "",
  CreatedBy: "",
  CreatedOn: "",
  DeliveryType: "",
  ModifiedBy: "",
  ModifiedOn: "",
  saved: false,
  hidden: false
}

export enum AssociatedDataType {
  Email = "EMAIL",
  Phone = "PHONE"
}

export enum DeliveryType {
  Text = "TEXT"
}

interface AdditionalAssociatedDataModalProps {
  show: boolean
  type: AssociatedDataType
  data: AssociatedData[]
  onClose: () => void
  onSave: (data: AssociatedData[]) => void
}

interface AdditionalAssociatedDataModalState {
  open: boolean
  showInactive: boolean
  data: AssociatedData[]
  isValid: boolean
  onlyOneCanBeActive: boolean
  showConfirmText: boolean
  selectedIndex: number
}

const isEmailValid = (value: string) => EmailRegexp.test(value)
const isPhoneValid = (value: string) => PhoneRegexp.test(value)

export default class AdditionalAssociatedDataModal extends Component<AdditionalAssociatedDataModalProps, AdditionalAssociatedDataModalState> {
  state: AdditionalAssociatedDataModalState = { 
    open: this.props.show, 
    showInactive: false, 
    data: [{ ...EmptyAssociatedData }], 
    isValid: true, 
    onlyOneCanBeActive: this.props.type === AssociatedDataType.Phone, 
    showConfirmText: false, 
    selectedIndex: -1 
  }

  componentDidUpdate(prevProps: AdditionalAssociatedDataModalProps) {
    if (this.props.show !== prevProps.show) {
      this.setState({ open: this.props.show, showInactive: false })
    }
    if (this.props.data !== prevProps.data) {
      this.setState({ data: [...this.props.data.map(item => ({...item, prevValue: item.ContactDetail})), { ...this.emptyAssociatedData() }] })
    }
  }

  emptyAssociatedData = () : AssociatedData => this.props.type === AssociatedDataType.Phone ? { ...EmptyAssociatedData, DeliveryType: DeliveryType.Text } : { ...EmptyAssociatedData }

  isValid = () => {
    const { data } = this.state
    const hasActive = data.filter(item => item.ContactDetail !== "").some(item => item.Active)
    const isUniq = Array.from(new Set(data.filter(item => !item.hidden).map(item => item.ContactDetail.toLowerCase()))).length === data.filter(item => !item.hidden).length
    if (!hasActive) {
      toast.error(`Please select at least one active ${this.props.type === AssociatedDataType.Email ? "email address" : "phone number"}`)
    }
    if (!isUniq) {
      toast.error(`Duplicate ${this.props.type === AssociatedDataType.Email ? "email address" : "phone number"} found`)
    }
    return hasActive && isUniq
  }

  handleClose = () => {
    const itemsOnInit = this.state.data.filter(data => data.ApplicationUserID > 0 || data.saved)
    this.resetAttributes(itemsOnInit)
    this.setState({ open: false, data: [...itemsOnInit, { ...this.emptyAssociatedData() }], selectedIndex: -1 })
    this.props.onClose()
  }

  resetAttributes = (items: AssociatedData[]) => {
    items.forEach(item => {
      item.hidden = false
      const relatedItem = this.props.data.find(el => el.ContactDetail === item.prevValue)
      if (relatedItem) {
        item.Active = relatedItem.Active
        item.DeliveryType = relatedItem.DeliveryType
        item.ContactDetail = relatedItem.ContactDetail
      }
    })
  }

  deactivateItems = (items: AssociatedData[], activeIndex: number) => {
    for (let i = 0; i < items.length; i++) {
      if (i != activeIndex) {
        items[i].Active = false
      }
    }
  }

  handleSave = () => {
    if (this.isValid()) {
      var itemsToValidate = this.state.data.filter(data => data.ContactDetail !== "" && !data.hidden && !data.saved && data.ApplicationUserID === 0)
      if(this.isNeedConfirmText(itemsToValidate)){
        this.setState({showConfirmText:true});
        return;
      }
      this.saveData();
    }
  }
  saveData = () => {
    var itemsToSave = this.state.data.filter(data => data.ContactDetail !== "" && !data.hidden)
    itemsToSave.forEach(data => data.saved = true)
    this.setState({ open: false, data: [...itemsToSave, { ...this.emptyAssociatedData() }], selectedIndex: -1   })
    this.props.onSave(itemsToSave)

  }

  canAddEmail = (email: string): boolean => email.length > 0 && isEmailValid(email)
  canAddMobile = (mobile: string): boolean => mobile.length > 0 && isPhoneValid(mobile)
  addRow = (index: number) => this.setState({ data: [...this.state.data, { ...this.emptyAssociatedData() }] })
  removeRow = (removedData: AssociatedData, index: number) => {
    removedData.hidden = true
    this.setState({ data: [...this.state.data] })
  }
  canAddRow = (data: AssociatedData) => !!data.DeliveryType.length && (this.props.type === AssociatedDataType.Email ? this.canAddEmail(data.ContactDetail) : this.canAddMobile(FormatPhone(data.ContactDetail)))
  validData = (data: AssociatedData) => !data.ContactDetail.length || this.canAddRow(data)

  changeContactDetail = (value: string, index: number) => {
    const newData = [...this.state.data]
    newData[index].ContactDetail = this.props.type === AssociatedDataType.Email ? value : value.replace(/\D/g, "")
    if (this.props.type === AssociatedDataType.Email) { newData[index].DeliveryType = this.props.type }
    this.setState({ data: newData, isValid: this.validData(newData[index]) })
  }

  toggleActive = (index: number) => {
    const newData = [...this.state.data]
    newData[index].Active = !newData[index].Active
    if (this.state.onlyOneCanBeActive && newData[index].Active) {
      this.deactivateItems(newData, index)
    }
    this.setState({ data: newData, isValid: this.validData(newData[index]), selectedIndex: index  })
  }
  
  toggleTextVoiceOption = (index: number, value: "TEXT" | "VOICE") => {
    if(value === "TEXT"){
      //this.setState({selectedIndex: index, showConfirmText:true});
      //return;
    }
    this.setTextVoiceOption(index, value);
  }
  textConfirmed = () => {
    this.setState({showConfirmText:false});
    this.saveData();
  }
  textNotConfirmed = () => {
    this.setState({showConfirmText:false});
  }
  setTextVoiceOption = (index: number, value: "TEXT" | "VOICE") => {
    const newData = [...this.state.data]
    newData[index].DeliveryType = value
    this.setState({ data: newData, isValid: this.validData(newData[index]), selectedIndex: index })
  }

  isNeedConfirmText = (items: AssociatedData[]) => {
    if (this.props.type != AssociatedDataType.Phone) {
      return false;
    }
    for (let i = 0; i < items.length; i++) {
      if (items[i].Active && items[i].DeliveryType === "TEXT") {
        return true;
      }
    }
    return false;
  }

  DataRow = (data: AssociatedData, index: number, editable: boolean, verified: boolean = false) => {
    if (data.hidden) {
      return <></>
    }
    return (
      <Row key={index} className="my-2" >
        <Col md={this.props.type === AssociatedDataType.Email ? 2 : 1}>{ this.props.type === AssociatedDataType.Email ? "Email Address" : "Phone" }</Col>
        <Col md={7}>
          { this.props.type === AssociatedDataType.Email && <Form.Control size="sm" type="text" value={data.ContactDetail} onChange={e => this.changeContactDetail(e.target.value, index)} className="rounded-pill" disabled={!editable} /> }
          { this.props.type === AssociatedDataType.Phone && <PhoneNumberMaskedInput size="sm" value={data.ContactDetail} onChange={e => this.changeContactDetail(e, index)} isInvalid={false} className="rounded-pill" disabled={!editable} /> }
        </Col>
        <Col md={1} className="px-0"><Checkbox id={`${verified ? "verified" : "new"}Active${index}`} label="Active" checked={data.Active} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.toggleActive(index)} /></Col>
        { this.props.type === AssociatedDataType.Phone && 
        <Col md={1} style={{paddingTop: "2px"}}>
          <Form.Check 
          type="radio" 
          inline 
          id={`${editable ? "new" : "existing"}TextOption${index}`} 
          label="Text" 
          name={`${editable ? "new" : "existing"}TextVoiceOption${index}`} 
          checked={data.DeliveryType === "TEXT"} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.toggleTextVoiceOption(index, "TEXT")} 
          className="abc-radio abc-radio-primary" />
          </Col> }
        { this.props.type === AssociatedDataType.Phone && 
        <Col md={1} style={{paddingTop: "2px"}}>
          <Form.Check 
          type="radio" 
          inline id={`${editable ? "new" : "existing"}VoiceOption${index}`} 
          label="Voice" name={`${editable ? "new" : "existing"}TextVoiceOption${index}`} 
          checked={data.DeliveryType === "VOICE"} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.toggleTextVoiceOption(index, "VOICE")} 
          className="abc-radio abc-radio-primary" />
          </Col> }
        <Col>
          { editable && ((this.state.data.length === index + 1) ?
          <Button variant="primary" className="btn-sm btn-sm-icon" onClick={ () => this.addRow(index) } disabled={ !this.canAddRow(data) }><FaPlus /></Button> :
          <Button variant="primary" className="btn-sm btn-sm-icon" onClick={ () => this.removeRow(data, index) }><FaMinus /></Button>) }
        </Col>
      </Row>
    )
  }

  render = () => {
    if (!this.state.open || !this.state.data.length) {
      return <></>
    }
    const { showInactive, data } = this.state
    const { type } = this.props
    const noInactive = !this.props.data.some(data => !data.Active)
    return (
      <>
      <Modal show={this.state.open} onHide={() => this.handleClose()} size="xl" backdrop="static" dialogClassName="dark">
        <Modal.Header closeButton>
          <Modal.Title>Enter { type === AssociatedDataType.Email ? "Additional Email Address" : "Phone Number" }</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          { !showInactive && this.props.data.map((data, index) => data.Active && !!data.CreatedOn.length && this.DataRow(this.state.data[index], index, false)) }
          { !showInactive && this.state.data.map((data, index) => !data.CreatedOn.length && this.DataRow(this.state.data[index], index, true)) }
          { showInactive && this.props.data.map((data, index) => !data.Active && !!data.CreatedOn.length && this.DataRow(this.state.data[index], index, false)) }
          { showInactive && noInactive && <p className="text-center">No Inactive { type === AssociatedDataType.Email ? "Email Address" : "Phone Number"} Found.</p> }
          { showInactive && <Row className="mt-auto">
            <Col><strong>Note: Inactive records will be displayed after the verification.</strong></Col>
          </Row> }
        </Modal.Body>
        <Modal.Footer>
          <Checkbox id="toggleActive" label={`Show Inactive ${type === AssociatedDataType.Email ? "Email Addresses" : "Phone Numbers"}`} checked={showInactive} onChange={() => this.setState({ showInactive: !this.state.showInactive })} className="mr-auto" />
          <Button size="sm" variant="secondary" onClick={() => this.handleClose()}>Cancel</Button>
          <Button size="sm" onClick={() => this.handleSave()} disabled={!this.state.isValid}>Confirm</Button>
        </Modal.Footer>
      </Modal>
        <ConfirmationModal show={this.state.showConfirmText}
          title="CONFIRM"
          textTitle="Be sure your phone accepts Text messaging."
          text=""
          showImage={false}
          onConfirm={() => this.textConfirmed()}
          onHide={() => this.textNotConfirmed()} />
      </>

    )
  }
}
